import NetxQueryClient, {
	type SuccessResponse,
	type QueryPager,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {
	getAssetProjectReviewDefaultSort,
	type IAssetProjectReview,
	type AssetProjectReviewSort,
} from '../schema';
import {getResponseResults} from '@netx/core-next/utils/api';

/**
 * `getReviewsForAsset` request params
 * (what is used to send to server)
 */
export type GetReviewsForAssetRequestOptions = {
	assetId: number;
	sort?: Partial<AssetProjectReviewSort>;
	startIndex?: number;
	stopIndex?: number;
};

/**
 * `getReviewsForAsset` request
 * (what gets sent to the server)
 */
export type GetReviewsForAssetRequest = [
	number,
	AssetProjectReviewSort,
	QueryPager,
];

/**
 * `getReviewsForAsset` response
 * (what comes back from the server)
 */
export type GetReviewsForAssetResponse =
	SuccessResponse<GetReviewsForAssetResult>;

/**
 * `getReviewsForAsset` result
 * (what is utilized from the server response)
 */
export type GetReviewsForAssetResult = {
	results: IAssetProjectReview[];
	size: number;
	startIndex: number;
};

export const getReviewsForAssetEndpoint = 'getReviewsForAsset';

/**
 * Get paged reviews for an asset
 * @param fetchParams Fetch params
 * @param signal Abort signal
 * @returns Promise of paged reviews for an asset
 *
 * @example
 * ```ts
 * const pagedGroups = await getReviewsForAsset({
 * 	sort: {
 * 		field: 'id',
 * 		order: 'asc',
 * 	},
 * 	startIndex: 0,
 * 	stopIndex: 10,
 * });
 * ```
 */
export const getReviewsForAsset = async (
	{
		assetId,
		sort,
		startIndex = 0,
		stopIndex = 25,
	}: GetReviewsForAssetRequestOptions,
	signal?: AbortSignal,
): Promise<GetReviewsForAssetResult> => {
	const client = NetxQueryClient();

	const requestParams: GetReviewsForAssetRequest = [
		assetId,
		getAssetProjectReviewDefaultSort(sort),
		{
			startIndex,
			size: stopIndex - startIndex,
		},
	];

	return client
		.post<GetReviewsForAssetResponse>(
			getReviewsForAssetEndpoint,
			requestParams,
			{
				signal,
			},
		)
		.then(getResponseResults);
};
